import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { showError, showSuccess } from "../components/ToastNotifications";
import CaptureFormView, { captureTheme, validateCapture } from "../components/leads/CaptureFormView";

const parseFields = (fields) => {
  if (Array.isArray(fields)) return fields;
  if (typeof fields !== "string") return null;
  try {
    const parsed = JSON.parse(fields);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const parseObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const parseArray = (value) => {
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const LeadCapturePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(null);
  const [brand, setBrand] = useState(null);
  const [values, setValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let active = true;
    apiRequest(`/api/public/leads/${encodeURIComponent(token)}`)
      .then((result) => {
        if (!active) return;
        const fields = parseFields(result.form?.fields);
        if (!result.form || !fields?.length) {
          setForm(null);
          setError("This lead capture form is not configured correctly.");
          return;
        }
        const settings = parseObject(result.form.settings);
        setForm({
          ...result.form,
          fields,
          settings: {
            ...settings,
            successMessage: settings.successMessage || "Thanks. Your details have been sent.",
            submitLabel: settings.submitLabel || "Send message"
          }
        });
        setBrand(result.brand ? {
          ...result.brand,
          colors: Array.isArray(result.brand.colors) ? result.brand.colors : parseArray(result.brand.colors)
        } : null);
        if (result.form.name) document.title = result.form.name;
      })
      .catch((requestError) => { if (active) setError(requestError.message || "This form is not available."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const setValue = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((current) => { const next = { ...current }; delete next[key]; return next; });
  };

  const submit = async (event) => {
    event.preventDefault();
    const found = validateCapture(form.fields, values);
    setFieldErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(`capture-${form.fields.find((field) => found[field.key])?.id}`)?.focus();
      return;
    }
    setSubmitting(true); setError("");
    const utm = Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].map((key) => [key, searchParams.get(key)]).filter(([, value]) => value));
    try {
      const result = await apiRequest(`/api/public/leads/${encodeURIComponent(token)}/submissions`, { method: "POST", body: JSON.stringify({ values, utm, campaign: searchParams.get("utm_campaign") || "", source: searchParams.get("source") || "Form Link", website: values.__company_website || "" }) });
      if (result?.success === false) throw new Error(result.message || "Unable to submit the form.");
      if (result.redirectUrl) { window.location.assign(result.redirectUrl); return; }
      const successMessage = typeof result.message === "string" && result.message.trim() ? result.message : form.settings.successMessage;
      setSuccess(successMessage);
      setValues({});
      showSuccess(successMessage);
    } catch (requestError) {
      const errorMessage = requestError.message || "Unable to submit the form.";
      setError(errorMessage);
      showError(errorMessage);
    }
    finally { setSubmitting(false); }
  };

  const { style } = captureTheme(form, brand);
  if (loading) return <main className="capture-page" style={style}><div className="capture-state" role="status"><i className="ti ti-loader-2 leads-spin" /><span>Loading form…</span></div></main>;
  if (!form) return <main className="capture-page" style={style}><section className="capture-state"><i className="ti ti-link-off" /><h1>Form unavailable</h1><p>{error || "This lead capture link has been disabled or removed."}</p></section></main>;
  return <main className="capture-page" style={style}>
    <CaptureFormView
      form={form} brand={brand} values={values} errors={fieldErrors} onChange={setValue} onSubmit={submit}
      submitting={submitting} error={error} success={success}
      honeypot={<div className="capture-honeypot" aria-hidden="true"><label htmlFor="lead-company-website">Company website</label><input id="lead-company-website" tabIndex="-1" autoComplete="off" value={values.__company_website || ""} onChange={(event) => setValues({ ...values, __company_website: event.target.value })} /></div>}
    />
  </main>;
};

export default LeadCapturePage;
